/**
 * ObradorDetail (P-01 — detail/edit) — fitxa de l'obrador.
 *
 * Editable: fiscal data (nom, raó social, NIF), pla, sector (action `update`).
 * State changes — suspend / reactivate / deactivate (soft delete) — each go
 * through a confirm dialog that REQUIRES a written reason (motiu obligatori),
 * invoking `tenant-update` with the matching action. The UI states that
 * operational data is preserved and the action is reversible.
 *
 * The super-user manages the tenant shell only — never its operational data
 * (multitenancy invariant). A deactivated tenant is read-only until reactivated.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Card, Field, useTheme } from '@obrava/ui';
import {
  TENANT_SECTORS,
  TENANT_PLANS,
  type TenantSector,
  type TenantPlan,
} from '@obrava/shared';
import { PageHeading, SectionTitle, LoadingView } from '../../components/layout.js';
import { Banner } from '../../components/Banner.js';
import { Badge } from '../../components/Badge.js';
import { Select } from '../../components/Select.js';
import { ReasonDialog } from '../../components/ReasonDialog.js';
import { useLanguage } from '../../providers/I18nProvider.js';
import { formatDate, formatDateTime } from '../../lib/format.js';
import type { AuditEntry } from '../../lib/edge.js';
import {
  sectorOptions,
  planOptions,
  statusLabel,
  auditActionLabel,
  auditEntityLabel,
  auditActionOptions,
  auditEntityOptions,
} from './labels.js';
import {
  useTenant,
  useUpdateTenant,
  useTenantOwner,
  useSendOwnerReset,
  useCreateOwner,
  useTenantAudit,
  type TenantRow,
  type AuditFilters,
} from './data.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Provision the owner account directly (no waiting for self-registration).
 *
 * Two modes, decided by whether the email is ALREADY in InsForge auth:
 *  - authExists: we can't set a fresh password (no admin API) — only link the
 *    existing account to this tenant and email a reset code. No name/password input.
 *  - new email: collect name + email + an OPTIONAL initial password the super-admin
 *    sets directly (else the owner gets a set-password email).
 */
function CreateOwnerForm(props: {
  readonly tenantId: string;
  readonly defaultEmail: string;
  readonly authExists: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const createOwner = useCreateOwner();
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState(props.defaultEmail);
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<{ name?: string; email?: string; password?: string } | null>(
    null,
  );
  const [banner, setBanner] = React.useState<
    { tone: 'success' | 'danger'; text: string } | null
  >(null);

  const PASSWORD_MIN = 6;
  const { authExists } = props;

  const successText = (res: {
    email: string;
    linked: boolean;
    passwordSet: boolean;
    credentialSent: boolean;
  }): string => {
    if (res.linked) return t('platformConsole.detail:ownerLinked', { email: res.email });
    if (res.passwordSet) return t('platformConsole.detail:ownerCreatedWithPassword', { email: res.email });
    if (res.credentialSent) return t('platformConsole.detail:ownerCreated', { email: res.email });
    return t('platformConsole.detail:ownerCreatedNoEmail', { email: res.email });
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBanner(null);
    const nextError: { name?: string; email?: string; password?: string } = {};
    if (!EMAIL_RE.test(email.trim())) nextError.email = t('platformConsole.detail:ownerCreateEmailInvalid');
    // Name + password only apply when creating a NEW account.
    if (!authExists) {
      if (!fullName.trim()) nextError.name = t('platformConsole.detail:ownerCreateNameRequired');
      if (password.length > 0 && password.length < PASSWORD_MIN) {
        nextError.password = t('platformConsole.detail:ownerCreatePasswordWeak');
      }
    }
    if (nextError.name || nextError.email || nextError.password) {
      setError(nextError);
      return;
    }
    setError(null);
    try {
      const res = await createOwner.mutateAsync({
        tenantId: props.tenantId,
        email: email.trim(),
        fullName: fullName.trim(),
        ...(!authExists && password.length > 0 ? { password } : {}),
      });
      setBanner({ tone: 'success', text: successText(res) });
    } catch {
      setBanner({ tone: 'danger', text: t('platformConsole.detail:ownerCreateError') });
    }
  };

  const hintStyle: React.CSSProperties = {
    margin: 0,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.footnote,
    color: theme.color.textSecondary,
  };

  // Already registered in InsForge: link + reset only (no name/password).
  if (authExists) {
    return (
      <form
        onSubmit={(e) => void onSubmit(e)}
        style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[4] }}
      >
        <Banner tone="info">
          {t('platformConsole.detail:ownerExistsLinkHint', { email: props.defaultEmail })}
        </Banner>
        {banner ? <Banner tone={banner.tone}>{banner.text}</Banner> : null}
        <div>
          <Button type="submit" disabled={createOwner.isPending}>
            {createOwner.isPending
              ? t('platformConsole.common:saving')
              : t('platformConsole.detail:ownerLinkCta')}
          </Button>
        </div>
      </form>
    );
  }

  // New email: create the account, optionally with an initial password.
  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[4] }}
    >
      <p style={hintStyle}>{t('platformConsole.detail:ownerCreateHint')}</p>
      <Field
        label={t('platformConsole.detail:ownerCreateName')}
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        error={error?.name}
      />
      <Field
        label={t('platformConsole.detail:ownerCreateEmail')}
        type="email"
        autoComplete="off"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={error?.email}
      />
      {/* Revealable: whoever sets this password then has to READ IT OUT to the new
          owner (see ownerCreatePasswordHint), so typing it blind is the wrong default.
          Mirrors the backoffice's add-user dialog. */}
      <Field
        label={t('platformConsole.detail:ownerCreatePassword')}
        type="password"
        autoComplete="new-password"
        revealable
        showPasswordLabel={t('common:showPassword')}
        hidePasswordLabel={t('common:hidePassword')}
        placeholder={t('platformConsole.detail:ownerCreatePasswordPlaceholder')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={error?.password}
      />
      <span style={hintStyle}>{t('platformConsole.detail:ownerCreatePasswordHint')}</span>
      {banner ? <Banner tone={banner.tone}>{banner.text}</Banner> : null}
      <div>
        <Button type="submit" disabled={createOwner.isPending}>
          {createOwner.isPending
            ? t('platformConsole.common:saving')
            : t('platformConsole.detail:ownerCreateCta')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Usuari principal (the obrador owner / tenant admin). Shows the owner identity +
 * account status. When no account exists the super-admin can PROVISION it
 * directly (CreateOwnerForm); when it exists, re-send a set-password code.
 *
 * The console never sets a password directly (no admin set-password API): it
 * always emails the owner a code so they set their own. No credential shown here.
 */
function OwnerCard(props: { readonly tenantId: string }): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const { language } = useLanguage();
  const owner = useTenantOwner(props.tenantId);
  const sendReset = useSendOwnerReset();
  const [banner, setBanner] = React.useState<
    { tone: 'success' | 'danger'; text: string } | null
  >(null);

  const OWNER_STATUS_KEY: Record<'active' | 'blocked' | 'invited' | 'none', string> = {
    active: 'platformConsole.detail:ownerStatusActive',
    blocked: 'platformConsole.detail:ownerStatusBlocked',
    invited: 'platformConsole.detail:ownerStatusInvited',
    none: 'platformConsole.detail:ownerStatusNone',
  };

  const onSendReset = async (): Promise<void> => {
    setBanner(null);
    try {
      const res = await sendReset.mutateAsync(props.tenantId);
      setBanner({
        tone: 'success',
        text: t('platformConsole.detail:ownerResetSent', { email: res.email }),
      });
    } catch {
      setBanner({ tone: 'danger', text: t('platformConsole.detail:ownerResetError') });
    }
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: theme.spacing.space[4],
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.footnote,
  };
  const labelStyle: React.CSSProperties = {
    minWidth: 120,
    color: theme.color.textSecondary,
  };

  return (
    <Card style={{ marginTop: theme.spacing.space[5] }}>
      <SectionTitle>{t('platformConsole.detail:sectionOwner')}</SectionTitle>
      {/* Pending, not isLoading: gated off (no session yet) must not read as
          "this obrador has no owner" — see the note in data.ts. */}
      {owner.isPending ? (
        <p style={{ ...rowStyle, color: theme.color.textSecondary }}>{t('common:loading')}</p>
      ) : owner.isError || !owner.data ? (
        <Banner tone="danger">{t('platformConsole.detail:ownerLoadError')}</Banner>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[4] }}>
          {owner.data.hasAccount ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[3] }}>
                {owner.data.fullName ? (
                  <div style={rowStyle}>
                    <span style={labelStyle}>{t('platformConsole.detail:ownerName')}</span>
                    <span style={{ color: theme.color.textPrimary }}>{owner.data.fullName}</span>
                  </div>
                ) : null}
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('platformConsole.detail:ownerEmail')}</span>
                  <span style={{ color: theme.color.textPrimary }}>{owner.data.email ?? '—'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('platformConsole.detail:ownerStatus')}</span>
                  <span style={{ color: theme.color.textPrimary }}>
                    {t(OWNER_STATUS_KEY[owner.data.status])}
                  </span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('platformConsole.detail:lastAccess')}</span>
                  <span style={{ color: theme.color.textPrimary }}>
                    {formatDate(owner.data.lastAccessAt, language) ??
                      t('platformConsole.common:never')}
                  </span>
                </div>
              </div>

              {banner ? <Banner tone={banner.tone}>{banner.text}</Banner> : null}

              {/* Re-send a set-password code for the existing account. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[3] }}>
                <Button
                  variant="secondary"
                  disabled={sendReset.isPending}
                  onClick={() => void onSendReset()}
                >
                  {sendReset.isPending
                    ? t('platformConsole.common:saving')
                    : t('platformConsole.detail:ownerResetCta')}
                </Button>
                <span style={{ ...rowStyle, color: theme.color.textSecondary }}>
                  {t('platformConsole.detail:ownerResetHint')}
                </span>
              </div>
            </>
          ) : (
            // No account yet ('invited' = admin email on the tenant, or 'none'):
            // provision it directly instead of waiting for self-registration.
            <CreateOwnerForm
              tenantId={props.tenantId}
              defaultEmail={owner.data.email ?? ''}
              authExists={owner.data.authExists}
            />
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * ActivityCard — the obrador's OWN activity log (F0.9). A platform super-user has
 * no tenant RLS grant, so it reads through the tenant-audit-log edge function.
 *
 * Transparency invariant: consulting this log is itself recorded — visibly to the
 * client — in the tenant's own audit_log (the server writes it on the first page).
 * The notice states this. Only metadata is shown (who / when / action / entity +
 * a derived name), never the raw before/after field values.
 */
function ActivityCard(props: { readonly tenantId: string }): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const { language } = useLanguage();
  const [action, setAction] = React.useState('all');
  const [entityType, setEntityType] = React.useState('all');

  const filters: AuditFilters = {
    action: action === 'all' ? null : action,
    entityType: entityType === 'all' ? null : entityType,
  };
  const query = useTenantAudit(props.tenantId, filters);
  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];

  const cellHeaderStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: `${theme.spacing.space[3]}px ${theme.spacing.space[4]}px`,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.caption,
    fontWeight: theme.typography.weight.semibold,
    color: theme.color.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: theme.typography.letterSpacing.wide,
    borderBottom: `1px solid ${theme.color.divider}`,
    whiteSpace: 'nowrap',
  };
  const cellStyle: React.CSSProperties = {
    padding: `${theme.spacing.space[4]}px`,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.footnote,
    color: theme.color.textPrimary,
    borderBottom: `1px solid ${theme.color.divider}`,
    verticalAlign: 'top',
  };

  const actionTone: Record<string, { bg: string; text: string }> = {
    create: { bg: theme.color.successBg, text: theme.color.successText },
    update: { bg: theme.color.infoBg, text: theme.color.infoText },
    delete: { bg: theme.color.warningBg, text: theme.color.warningText },
    restore: { bg: theme.color.accentSubtle, text: theme.color.accent },
    support_view: { bg: theme.color.accentSubtle, text: theme.color.accent },
  };
  const pill = (tone: { bg: string; text: string }, label: string): React.ReactElement => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: theme.spacing.size.chipHeight,
        paddingInline: theme.spacing.space[4],
        backgroundColor: tone.bg,
        color: tone.text,
        borderRadius: theme.radii.radius.pill,
        fontSize: theme.typography.size.footnote,
        fontWeight: theme.typography.weight.medium,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );

  const actorText = (row: AuditEntry): string => {
    if (row.actor_kind === 'support') return t('domain.audit:actorSupport');
    if (row.actor_kind === 'system') return t('domain.audit:actorSystem');
    return row.actor_name ?? '—';
  };

  const neutral = { bg: theme.color.neutralBg, text: theme.color.neutralText };

  return (
    <Card style={{ marginTop: theme.spacing.space[7] }}>
      <SectionTitle>{t('platformConsole.detail:sectionActivity')}</SectionTitle>
      <p
        style={{
          margin: `0 0 ${theme.spacing.space[4]}px`,
          fontFamily: theme.typography.family.sans,
          fontSize: theme.typography.size.footnote,
          color: theme.color.textSecondary,
        }}
      >
        {t('platformConsole.detail:activityNotice')}
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: theme.spacing.space[4],
          marginBottom: theme.spacing.space[4],
        }}
      >
        <Select
          label={t('domain.audit:colAction')}
          value={action}
          onChange={(e) => setAction(e.target.value)}
          options={auditActionOptions(t)}
          containerStyle={{ minWidth: 180 }}
        />
        <Select
          label={t('domain.audit:colEntity')}
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          options={auditEntityOptions(t)}
          containerStyle={{ minWidth: 180 }}
        />
      </div>

      {query.isError ? (
        <Banner tone="danger">{t('platformConsole.detail:activityError')}</Banner>
      ) : query.isPending ? (
        <p style={{ color: theme.color.textSecondary }}>{t('common:loading')}</p>
      ) : entries.length === 0 ? (
        <p style={{ color: theme.color.textSecondary }}>
          {t('platformConsole.detail:activityEmpty')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[4] }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={cellHeaderStyle}>{t('domain.audit:colWhen')}</th>
                  <th style={cellHeaderStyle}>{t('domain.audit:colWho')}</th>
                  <th style={cellHeaderStyle}>{t('domain.audit:colAction')}</th>
                  <th style={cellHeaderStyle}>{t('domain.audit:colEntity')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr key={row.id}>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.created_at, language) ?? '—'}
                    </td>
                    <td style={cellStyle}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: theme.spacing.space[3],
                        }}
                      >
                        {actorText(row)}
                        {row.actor_kind === 'support'
                          ? pill(
                              { bg: theme.color.accentSubtle, text: theme.color.accent },
                              t('domain.audit:supportBadge'),
                            )
                          : null}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      {pill(actionTone[row.action] ?? neutral, auditActionLabel(t, row.action))}
                    </td>
                    <td style={cellStyle}>
                      {auditEntityLabel(t, row.entity_type)}
                      {row.entity_name ? (
                        <span style={{ color: theme.color.textSecondary }}>
                          {` · ${row.entity_name}`}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {query.hasNextPage ? (
            <div>
              <Button
                variant="secondary"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {t('platformConsole.detail:activityLoadMore')}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/** Editable fiscal/plan form. Reason-gated actions are handled separately. */
const EditSchema = z
  .object({
    name: z.string().trim().min(1),
    legal_name: z.string().trim().optional(),
    tax_id: z.string().trim().optional(),
    sector: z.enum(TENANT_SECTORS),
    plan: z.enum(TENANT_PLANS),
  })
  .strict();
type EditValues = z.infer<typeof EditSchema>;

type DialogKind = 'update' | 'suspend' | 'reactivate' | 'soft_delete' | null;

function EditForm(props: { readonly tenant: TenantRow }): React.ReactElement {
  const { tenant } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const updateTenant = useUpdateTenant();

  const [dialog, setDialog] = React.useState<DialogKind>(null);
  const [banner, setBanner] = React.useState<
    { tone: 'success' | 'danger'; text: string } | null
  >(null);
  // Validated edit values awaiting a documented reason before they are saved.
  const [pendingEdit, setPendingEdit] = React.useState<EditValues | null>(null);

  const readOnly = tenant.status === 'deactivated';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<EditValues>({
    resolver: zodResolver(EditSchema),
    defaultValues: {
      name: tenant.name,
      legal_name: tenant.legal_name ?? '',
      tax_id: tenant.tax_id ?? '',
      sector: tenant.sector,
      plan: tenant.plan,
    },
  });

  // Submitting the fiscal/plan form does not save directly: the `tenant_update`
  // audit action REQUIRES a written reason (motiu del canvi), so we stash the
  // validated values and open the reason dialog. The save runs on confirm.
  const onSave = handleSubmit((values) => {
    setBanner(null);
    setPendingEdit(values);
    setDialog('update');
  });

  const saveEdit = async (values: EditValues, reason: string): Promise<void> => {
    setBanner(null);
    setDialog(null);
    setPendingEdit(null);
    try {
      const updated = await updateTenant.mutateAsync({
        tenantId: tenant.id,
        action: 'update',
        reason,
        patch: {
          name: values.name,
          ...(values.legal_name ? { legal_name: values.legal_name } : {}),
          ...(values.tax_id ? { tax_id: values.tax_id } : {}),
          sector: values.sector as TenantSector,
          plan: values.plan as TenantPlan,
        },
      });
      reset({
        name: updated.name,
        legal_name: updated.legal_name ?? '',
        tax_id: updated.tax_id ?? '',
        sector: updated.sector,
        plan: updated.plan,
      });
      setBanner({ tone: 'success', text: t('platformConsole.detail:saved') });
    } catch {
      setBanner({ tone: 'danger', text: t('platformConsole.detail:saveError') });
    }
  };

  const runAction = async (
    action: 'suspend' | 'reactivate' | 'soft_delete',
    reason: string,
  ): Promise<void> => {
    setBanner(null);
    setDialog(null);
    try {
      const updated = await updateTenant.mutateAsync({
        tenantId: tenant.id,
        action,
        reason,
      });
      if (action === 'soft_delete') {
        void navigate({ to: '/tenants' });
        return;
      }
      setBanner({
        tone: 'success',
        text: `${statusLabel(t, updated.status)}`,
      });
    } catch {
      setBanner({ tone: 'danger', text: t('platformConsole.common:actionError') });
    }
  };

  const dialogConfig = {
    update: {
      title: t('platformConsole.detail:saveTitle'),
      body: t('platformConsole.detail:saveBody'),
      tone: 'info' as const,
      confirm: t('platformConsole.detail:save'),
    },
    suspend: {
      title: t('platformConsole.detail:suspendTitle'),
      body: t('platformConsole.detail:suspendBody'),
      tone: 'warning' as const,
      confirm: t('platformConsole.detail:suspend'),
    },
    reactivate: {
      title: t('platformConsole.detail:reactivateTitle'),
      body: t('platformConsole.detail:reactivateBody'),
      tone: 'info' as const,
      confirm: t('platformConsole.detail:reactivate'),
    },
    soft_delete: {
      title: t('platformConsole.detail:deactivateTitle'),
      body: t('platformConsole.detail:deactivateBody'),
      tone: 'danger' as const,
      confirm: t('platformConsole.detail:deactivate'),
    },
  };
  const active = dialog ? dialogConfig[dialog] : null;

  return (
    <div>
      <PageHeading
        title={tenant.name}
        subtitle={t('platformConsole.detail:title')}
        action={<Badge status={tenant.status} label={statusLabel(t, tenant.status)} />}
      />

      {tenant.status === 'deactivated' ? (
        <Banner tone="danger" style={{ marginBottom: theme.spacing.space[5] }}>
          {t('platformConsole.detail:deletedBanner')}
        </Banner>
      ) : tenant.status === 'suspended' ? (
        <Banner tone="warning" style={{ marginBottom: theme.spacing.space[5] }}>
          {t('platformConsole.detail:suspendedBanner')}
        </Banner>
      ) : null}

      <form onSubmit={(e) => void onSave(e)} style={{ maxWidth: theme.spacing.size.contentMaxWidth }}>
        <Card style={{ marginBottom: theme.spacing.space[5] }}>
          <SectionTitle>{t('platformConsole.detail:sectionFiscal')}</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[5] }}>
            <Field
              label={t('platformConsole.detail:name')}
              error={errors.name?.message}
              disabled={readOnly}
              {...register('name')}
            />
            <Field
              label={t('platformConsole.detail:legalName')}
              error={errors.legal_name?.message}
              disabled={readOnly}
              {...register('legal_name')}
            />
            <Field
              label={t('platformConsole.detail:taxId')}
              error={errors.tax_id?.message}
              disabled={readOnly}
              {...register('tax_id')}
            />
          </div>
        </Card>

        <Card style={{ marginBottom: theme.spacing.space[5] }}>
          <SectionTitle>{t('platformConsole.detail:sectionPlan')}</SectionTitle>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: theme.spacing.space[5],
            }}
          >
            <Select
              label={t('platformConsole.detail:sector')}
              options={sectorOptions(t)}
              error={errors.sector?.message}
              disabled={readOnly}
              {...register('sector')}
            />
            <Select
              label={t('platformConsole.detail:plan')}
              options={planOptions(t)}
              error={errors.plan?.message}
              disabled={readOnly}
              {...register('plan')}
            />
          </div>
          <div
            style={{
              display: 'flex',
              gap: theme.spacing.space[6],
              marginTop: theme.spacing.space[5],
              fontFamily: theme.typography.family.sans,
              fontSize: theme.typography.size.footnote,
              color: theme.color.textSecondary,
            }}
          >
            <span>
              {t('platformConsole.detail:createdAt')}:{' '}
              {formatDate(tenant.created_at, language) ?? '—'}
            </span>
            <span>
              {t('platformConsole.detail:lastAccess')}:{' '}
              {formatDate(tenant.last_access_at, language) ??
                t('platformConsole.common:never')}
            </span>
          </div>
        </Card>

        {banner ? (
          <Banner tone={banner.tone} style={{ marginBottom: theme.spacing.space[5] }}>
            {banner.text}
          </Banner>
        ) : null}

        {!readOnly ? (
          <div style={{ display: 'flex', gap: theme.spacing.space[4] }}>
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting
                ? t('platformConsole.common:saving')
                : t('platformConsole.detail:save')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void navigate({ to: '/tenants' })}
            >
              {t('common:back')}
            </Button>
          </div>
        ) : null}
      </form>

      {/* Usuari principal (owner) — identity + send a fresh access code. */}
      <OwnerCard tenantId={tenant.id} />

      {/* Registre d'activitat — the obrador's own audit log (access is logged). */}
      <ActivityCard tenantId={tenant.id} />

      {/* State changes — each requires a documented reason (motiu obligatori). */}
      <Card style={{ marginTop: theme.spacing.space[7] }}>
        <SectionTitle>{t('platformConsole.detail:sectionStatus')}</SectionTitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing.space[4] }}>
          {tenant.status === 'active' ? (
            <Button variant="secondary" onClick={() => setDialog('suspend')}>
              {t('platformConsole.detail:suspend')}
            </Button>
          ) : null}
          {tenant.status === 'suspended' || tenant.status === 'deactivated' ? (
            <Button variant="secondary" onClick={() => setDialog('reactivate')}>
              {t('platformConsole.detail:reactivate')}
            </Button>
          ) : null}
        </div>
      </Card>

      {tenant.status !== 'deactivated' ? (
        <Card
          style={{
            marginTop: theme.spacing.space[5],
            borderColor: theme.color.danger,
          }}
        >
          <SectionTitle>{t('platformConsole.detail:sectionDanger')}</SectionTitle>
          <Banner tone="danger" style={{ marginBottom: theme.spacing.space[5] }}>
            {t('platformConsole.detail:deactivateBody')}
          </Banner>
          <Button variant="secondary" onClick={() => setDialog('soft_delete')}>
            {t('platformConsole.detail:deactivate')}
          </Button>
        </Card>
      ) : null}

      <ReasonDialog
        open={dialog !== null}
        title={active?.title ?? ''}
        body={active?.body ?? ''}
        tone={active?.tone ?? 'info'}
        confirmLabel={active?.confirm ?? t('common:confirm')}
        confirmDisabled={updateTenant.isPending}
        onCancel={() => {
          setDialog(null);
          setPendingEdit(null);
        }}
        onConfirm={(reason) => {
          if (dialog === 'update') {
            if (pendingEdit) void saveEdit(pendingEdit, reason);
          } else if (dialog) {
            void runAction(dialog, reason);
          }
        }}
      />
    </div>
  );
}

export function ObradorDetail(): React.ReactElement {
  const { t } = useTranslation();
  const { id } = useParams({ from: '/tenants/$id' });
  // Pending covers "gated off" too — see the note in ObradorsList.
  const { data, isPending, isError } = useTenant(id);

  if (isPending) {
    return <LoadingView label={t('common:loading')} />;
  }
  if (isError || !data) {
    return <Banner tone="danger">{t('platformConsole.detail:notFound')}</Banner>;
  }
  return <EditForm tenant={data} />;
}
